/**
 * BookingDetailPage - Admin detail view of a booking
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getBooking,
  cancelBooking,
  processRefund,
  addBookingNote,
  updateBookingNote,
  deleteBookingNote,
  updateBooking,
  sendMarketingMaterialsReminder,
  getAdminFaculty,
  assignFacultyToClass,
  unassignFacultyFromClass,
  markFacultyPaidExternal,
  completeClass,
  updateAdminClass,
  reconcileBookingBalance,
  sendDepositLink,
  sendBalanceLink,
  getStudioSavedCard,
  formatCurrency,
  type Booking,
  type BookingNote,
  type Faculty,
  type AdminClass,
  type UpdateClassInput,
  type UpdateBookingInput,
} from '../../../api/admin';
import { formatTimeDisplay } from '@sda/shared';
import { formatClassLevel } from '../../../utils/classLevel';
import { todayInTimezone } from '../../../utils/timezone';
import { buildFacultyHeadshotRows } from '../../../utils/headshotDownload';
import { MARKETING_STATUS_OPTIONS, GROUP_TICKET_STATUS_OPTIONS } from '../../../utils/requestStatus';
import ConfirmModal from '../../../components/ui/ConfirmModal';
import { describeSavedCard, type SavedCard } from '../../../api/studios';
import { BalanceSchedule } from '../../../components/booking/BalanceSchedule';

// SDA-215: human-readable labels for non-active faculty onboarding states.
// Shared by the Assign Faculty dropdown option and the confirm-step note so the
// wording can't drift between the two.
const FACULTY_STATUS_LABELS: Record<string, string> = {
  invited: 'Invited',
  onboarding: 'Onboarding',
  pending_approval: 'Pending approval',
};

// SDA-263: this page is the only edit surface for request fulfillment
// statuses — the dashboard panels render them as read-only badges (from the
// same shared label source) and drop rows once they're Complete.

export default function BookingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  // SDA-296: the class pending a per-class "Mark Class Complete" confirmation.
  const [completeClassTarget, setCompleteClassTarget] = useState<AdminClass | null>(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [reconcileAction, setReconcileAction] = useState<'mark_paid' | 'waive' | null>(null);

  // Form states
  const [cancelReason, setCancelReason] = useState('');
  // SDA-253: whether cancelling emails the studio — defaults ON to preserve
  // existing behavior (matches the server's zod default of true).
  const [notifyStudio, setNotifyStudio] = useState(true);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  // Refund errors render INSIDE the modal — the page-level banner is hidden
  // behind the modal overlay.
  const [refundError, setRefundError] = useState<string | null>(null);
  const [reconcileReference, setReconcileReference] = useState('');
  const [reconcileNote, setReconcileNote] = useState('');
  // SDA-232: optional partial amount (dollars) for mark_paid. Blank = full remaining.
  const [reconcileAmount, setReconcileAmount] = useState('');
  const [newNote, setNewNote] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  // Marketing materials reminder states
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [reminderSuccess, setReminderSuccess] = useState(false);
  // SDA-263: which request-status select (if any) has a save in flight.
  const [savingRequestStatus, setSavingRequestStatus] = useState<'marketing' | 'group' | null>(null);

  // Group tickets confirm modal state
  const [groupTicketsConfirm, setGroupTicketsConfirm] = useState<{ action: 'add' | 'remove' } | null>(null);

  // SDA-232: confirm-gate for emailing the studio a deposit payment link.
  const [showDepositLinkConfirm, setShowDepositLinkConfirm] = useState(false);
  const [showBalanceLinkConfirm, setShowBalanceLinkConfirm] = useState(false);

  // Faculty assignment states
  const [showFacultyModal, setShowFacultyModal] = useState(false);
  const [selectedClassForFaculty, setSelectedClassForFaculty] = useState<{
    id: string;
    showId?: string;
    showName?: string;
  } | null>(null);
  const [availableFaculty, setAvailableFaculty] = useState<Faculty[]>([]);
  const [selectedFacultyId, setSelectedFacultyId] = useState('');
  // SDA-277: class pending an unassign confirmation (faculty backed out).
  const [unassignTarget, setUnassignTarget] = useState<{ classId: string; facultyName: string } | null>(null);
  // SDA-276: class pending a "mark paid externally" confirmation.
  const [markPaidTarget, setMarkPaidTarget] = useState<{ classId: string; facultyName: string } | null>(null);
  const [markPaidReference, setMarkPaidReference] = useState('');
  const [markPaidNote, setMarkPaidNote] = useState('');

  // Session preferences edit states
  const [editingSession, setEditingSession] = useState(false);
  const [sessionPref, setSessionPref] = useState<'early' | 'default' | 'late'>('default');
  const [sessionSimultaneous, setSessionSimultaneous] = useState(false);
  const [sessionTrigger, setSessionTrigger] = useState<'matinee_threshold' | 'manual_checkbox' | 'distance_threshold' | null>(null);
  const [savingSession, setSavingSession] = useState(false);
  const [loadingFaculty, setLoadingFaculty] = useState(false);

  // Class edit states
  const [showClassModal, setShowClassModal] = useState(false);
  const [editingClassId, setEditingClassId] = useState<string | null>(null);
  const [classDate, setClassDate] = useState('');
  const [classStartTime, setClassStartTime] = useState('');
  const [classEndTime, setClassEndTime] = useState('');
  const [classLevel, setClassLevel] = useState<'all_levels' | 'beginner' | 'intermediate' | 'advanced'>('all_levels');
  const [classStudents, setClassStudents] = useState('');
  const [savingClass, setSavingClass] = useState(false);
  const [classError, setClassError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadBooking(id);
    }
  }, [id]);

  // Card on file (SDA-292) — loaded separately so a Stripe hiccup never
  // blocks the booking page.
  const studioIdForCard = booking?.studio?.id;
  const [savedCard, setSavedCard] = useState<{ status: 'loading' } | { status: 'error' } | { status: 'loaded'; card: SavedCard | null }>({ status: 'loading' });
  useEffect(() => {
    if (!studioIdForCard) return;
    let cancelled = false;
    setSavedCard({ status: 'loading' });
    getStudioSavedCard(studioIdForCard)
      .then((card) => !cancelled && setSavedCard({ status: 'loaded', card }))
      .catch(() => !cancelled && setSavedCard({ status: 'error' }));
    return () => {
      cancelled = true;
    };
  }, [studioIdForCard]);

  const loadBooking = async (bookingId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getBooking(bookingId);
      setBooking(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load booking');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!id || !cancelReason.trim()) return;

    try {
      setIsProcessing(true);
      await cancelBooking(id, { reason: cancelReason, notify_studio: notifyStudio });
      await loadBooking(id);
      setShowCancelModal(false);
      setCancelReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel booking');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRefund = async () => {
    if (!id || !refundAmount || !refundReason.trim()) return;

    const amountCents = Math.round(parseFloat(refundAmount) * 100);
    if (isNaN(amountCents) || amountCents <= 0) {
      setRefundError('Please enter a valid refund amount');
      return;
    }
    if (amountCents > maxRefundable) {
      setRefundError(`Amount cannot exceed the maximum refundable of ${formatCurrency(maxRefundable)}.`);
      return;
    }

    try {
      setIsProcessing(true);
      setRefundError(null);
      const result = await processRefund(id, { amount_cents: amountCents, reason: refundReason });
      await loadBooking(id);
      setShowRefundModal(false);
      setRefundAmount('');
      setRefundReason('');
      setSuccess(`Refund of ${formatCurrency(result.total_refunded_cents)} processed.`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      // A partial-failure response still moved money — reload so the page
      // shows what actually refunded before surfacing the error.
      await loadBooking(id);
      setRefundError(err instanceof Error ? err.message : 'Failed to process refund');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReconcileBalance = async () => {
    if (!id || !reconcileAction) return;

    // SDA-232: optional partial amount only applies to mark_paid. Blank keeps
    // legacy behavior (full remaining, server-computed). Validate a provided
    // amount is > 0 and <= remaining balance before submitting.
    let amountCents: number | undefined;
    if (reconcileAction === 'mark_paid' && reconcileAmount.trim() !== '') {
      const dollars = Number(reconcileAmount);
      const balanceDueDollars = Math.max(0, balanceDue) / 100;
      if (!Number.isFinite(dollars) || dollars <= 0) {
        setError('Enter an amount greater than zero, or leave it blank to record the full balance.');
        return;
      }
      if (dollars > balanceDueDollars) {
        setError('Amount cannot exceed the remaining balance.');
        return;
      }
      amountCents = Math.round(dollars * 100);
    }

    try {
      setIsProcessing(true);
      const result = await reconcileBookingBalance(id, {
        action: reconcileAction,
        reference: reconcileReference.trim() || undefined,
        note: reconcileNote.trim() || undefined,
        amount_cents: amountCents,
      });
      await loadBooking(id);
      setReconcileAction(null);
      setReconcileReference('');
      setReconcileNote('');
      setReconcileAmount('');
      setSuccess(
        result.balance_status === 'waived'
          ? `Balance of ${formatCurrency(result.amount_cents)} waived.`
          : result.balance_status === 'pending'
          ? `Recorded ${formatCurrency(result.amount_cents)} (external). Remaining balance is still due.`
          : `Balance of ${formatCurrency(result.amount_cents)} marked paid (external).`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reconcile balance');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendDepositLink = async () => {
    if (!id) return;
    setShowDepositLinkConfirm(false);
    try {
      setIsProcessing(true);
      const result = await sendDepositLink(id);
      setSuccess(`Deposit payment link sent to ${result.email}.`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send deposit payment link');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendBalanceLink = async () => {
    if (!id) return;
    setShowBalanceLinkConfirm(false);
    try {
      setIsProcessing(true);
      const result = await sendBalanceLink(id);
      setSuccess(`Balance payment link sent to ${result.email}.`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send balance payment link');
    } finally {
      setIsProcessing(false);
    }
  };

  // SDA-263: save a fulfillment-status change from one of the request-status
  // selects and merge the server's response into local booking state.
  const handleRequestStatusChange = async (
    kind: 'marketing' | 'group',
    input: Pick<UpdateBookingInput, 'marketing_request_status' | 'group_ticket_status'>,
  ) => {
    if (!id) return;
    setSavingRequestStatus(kind);
    try {
      const updated = await updateBooking(id, input);
      setBooking((prev) => (prev ? { ...prev, ...updated } : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update request status');
    } finally {
      setSavingRequestStatus(null);
    }
  };

  const handleAddNote = async () => {
    if (!id || !newNote.trim()) return;

    try {
      setIsProcessing(true);
      await addBookingNote(id, newNote);
      await loadBooking(id);
      setShowNoteModal(false);
      setNewNote('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add note');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdateNote = async (noteId: string) => {
    if (!id || !editingNoteText.trim()) return;

    try {
      setIsProcessing(true);
      await updateBookingNote(id, noteId, editingNoteText);
      await loadBooking(id);
      setEditingNoteId(null);
      setEditingNoteText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update note');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!id || !window.confirm('Delete this note?')) return;

    try {
      setIsProcessing(true);
      await deleteBookingNote(id, noteId);
      await loadBooking(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete note');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOpenFacultyModal = async (cls: {
    id: string;
    scheduled_date?: string;
    time_slot?: string;
    faculty?: { id?: string } | null;
    tour_stop?: {
      show?: { id?: string; name?: string };
    };
  }) => {
    setSelectedClassForFaculty({
      id: cls.id,
      showId: cls.tour_stop?.show?.id,
      showName: cls.tour_stop?.show?.name,
    });
    setSelectedFacultyId('');
    setShowFacultyModal(true);
    setLoadingFaculty(true);

    try {
      // Fetch faculty, optionally filtered by show, excluding those busy at this time
      const result = await getAdminFaculty({
        show_id: cls.tour_stop?.show?.id,
        // SDA-215: include faculty who haven't finished onboarding yet (invited/
        // onboarding/pending_approval), not just status='active'. The dropdown
        // surfaces each member's status so the admin sees their onboarding state.
        assignable: true,
        exclude_date: cls.scheduled_date,
        exclude_time: cls.time_slot,
        exclude_class_id: cls.id,
        exclude_faculty_id: cls.faculty?.id || undefined,
      });
      setAvailableFaculty(result.faculty);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load faculty');
    } finally {
      setLoadingFaculty(false);
    }
  };

  const handleAssignFaculty = async () => {
    if (!selectedFacultyId || !selectedClassForFaculty) return;

    try {
      setIsProcessing(true);
      await assignFacultyToClass(selectedFacultyId, selectedClassForFaculty.id);
      await loadBooking(id!);
      setShowFacultyModal(false);
      setSelectedClassForFaculty(null);
      setSelectedFacultyId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign faculty');
    } finally {
      setIsProcessing(false);
    }
  };

  // SDA-277: clear the assignment so the class returns to Unassigned. Fired
  // from the ConfirmModal — no emails; the replacement's acceptance re-notifies.
  const handleUnassignFaculty = async () => {
    if (!unassignTarget || !id) return;

    try {
      setIsProcessing(true);
      await unassignFacultyFromClass(unassignTarget.classId);
      await loadBooking(id);
      setUnassignTarget(null);
    } catch (err) {
      setUnassignTarget(null);
      setError(err instanceof Error ? err.message : 'Failed to unassign faculty');
    } finally {
      setIsProcessing(false);
    }
  };

  // SDA-296: complete ONE class (multi-class bookings). The server queues that
  // class's faculty payout and closes the booking itself after the last class
  // when nothing is owed.
  const handleCompleteClass = async () => {
    if (!completeClassTarget || !id || !booking) return;
    const target = completeClassTarget;
    const previewRow = (booking.payout_preview ?? []).find((r) => r.class_id === target.id);

    try {
      setError(null);
      const result = await completeClass(target.id);
      setCompleteClassTarget(null);
      await loadBooking(id);

      let message = 'Class marked complete.';
      if (result.payout === 'created' && previewRow) {
        message = `Class marked complete. ${previewRow.faculty_name}'s ${formatCurrency(previewRow.amount_cents)} payout will be sent within about 15 minutes.`;
      } else if (result.payout === 'created') {
        message = 'Class marked complete. The faculty payout will be sent within about 15 minutes.';
      } else if (result.payout === 'exists') {
        message = 'Class marked complete. A payout already exists for this class.';
      } else if (result.skipped_payouts.length > 0) {
        const why =
          result.skipped_payouts[0]!.reason === 'Stripe account not payout-ready'
            ? "the faculty's Stripe account isn't set up for payouts yet"
            : "the payout couldn't be created. Check the Payouts page";
        message = `Class marked complete. No payout created — ${why}.`;
      }
      if (result.booking_completed) {
        message = `${message} That was the last class — the booking is now Completed.`;
      } else if (result.booking_open_reason === 'balance_open') {
        message = `${message} All classes are complete. The booking stays Confirmed until the ${formatCurrency(Math.max(0, booking.balance_due_cents))} balance is settled.`;
      }
      setSuccess(message);
      setTimeout(() => setSuccess(null), 8000);
    } catch (err) {
      setCompleteClassTarget(null);
      const message = err instanceof Error ? err.message : 'Failed to mark class complete';
      // A refusal usually means the class changed elsewhere (completed or
      // cancelled in another tab) — reload so the page matches, then show why.
      await loadBooking(id);
      setError(message);
    }
  };

  // SDA-276: record that faculty for this class were already paid outside the
  // system (e.g. via QBO). No money moves — this only prevents Mark Complete
  // and the payout cron from scheduling a real Stripe payout for this class.
  const handleMarkFacultyPaidExternal = async () => {
    if (!markPaidTarget || !id) return;

    try {
      setIsProcessing(true);
      await markFacultyPaidExternal(markPaidTarget.classId, {
        reference: markPaidReference.trim() || undefined,
        note: markPaidNote.trim() || undefined,
      });
      await loadBooking(id);
      setMarkPaidTarget(null);
      setMarkPaidReference('');
      setMarkPaidNote('');
      setSuccess(`${markPaidTarget.facultyName} marked paid externally.`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark faculty paid');
    } finally {
      setIsProcessing(false);
    }
  };

  // Normalize a stored time (HH:MM:SS or HH:MM) to the HH:MM that <input type="time"> expects.
  const toTimeInputValue = (time: string | null | undefined): string =>
    time ? time.slice(0, 5) : '';

  // Map a stored class_level to the canonical edit enum; unknown/legacy → 'all_levels'.
  const toLevelValue = (level: string | null | undefined): 'all_levels' | 'beginner' | 'intermediate' | 'advanced' => {
    if (level === 'beginner' || level === 'intermediate' || level === 'advanced') return level;
    return 'all_levels';
  };

  const handleOpenClassModal = (cls: AdminClass) => {
    setEditingClassId(cls.id);
    setClassDate(cls.scheduled_date ? cls.scheduled_date.slice(0, 10) : '');
    setClassStartTime(toTimeInputValue(cls.start_time ?? cls.time_slot));
    setClassEndTime(toTimeInputValue(cls.end_time));
    setClassLevel(toLevelValue(cls.class_level ?? cls.student_level));
    setClassStudents(
      cls.expected_students != null
        ? String(cls.expected_students)
        : cls.student_count != null
          ? String(cls.student_count)
          : '',
    );
    setClassError(null);
    setShowClassModal(true);
  };

  const handleSaveClass = async () => {
    if (!editingClassId || !booking) return;
    const original = booking.classes?.find((c) => c.id === editingClassId);
    if (!original) return;

    // Build a payload of only the fields the admin actually changed.
    const payload: UpdateClassInput = {};
    const originalDate = original.scheduled_date ? original.scheduled_date.slice(0, 10) : '';
    if (classDate && classDate !== originalDate) payload.class_date = classDate;

    const originalStart = toTimeInputValue(original.start_time ?? original.time_slot);
    if (classStartTime && classStartTime !== originalStart) payload.start_time = classStartTime;

    const originalEnd = toTimeInputValue(original.end_time);
    if (classEndTime && classEndTime !== originalEnd) payload.end_time = classEndTime;

    const originalLevel = toLevelValue(original.class_level ?? original.student_level);
    if (classLevel !== originalLevel) payload.class_level = classLevel;

    const originalStudents = original.expected_students ?? original.student_count ?? null;
    if (classStudents !== '') {
      const parsed = parseInt(classStudents, 10);
      if (Number.isNaN(parsed) || parsed <= 0) {
        setClassError('Expected students must be a positive number');
        return;
      }
      if (parsed !== originalStudents) payload.expected_students = parsed;
    }

    if (Object.keys(payload).length === 0) {
      setClassError('No changes to save');
      return;
    }

    try {
      setSavingClass(true);
      setClassError(null);
      await updateAdminClass(editingClassId, payload);
      await loadBooking(id!);
      setShowClassModal(false);
      setEditingClassId(null);
    } catch (err) {
      setClassError(err instanceof Error ? err.message : 'Failed to update class');
    } finally {
      setSavingClass(false);
    }
  };

  // SDA-256: these must be the real booking_status enum values. The previous map
  // keyed off 'pending_deposit'/'pending_balance', which do not exist, so every
  // real status except confirmed/completed/cancelled fell through to raw text.
  // Styles + labels are kept identical to BookingsListPage so the pages agree.
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-warning-100 text-warning-700',
      deposit_paid: 'bg-primary-100 text-primary-700',
      confirmed: 'bg-success-100 text-success-700',
      balance_paid: 'bg-success-100 text-success-700',
      completed: 'bg-warm-100 text-warm-600',
      cancelled: 'bg-danger-100 text-danger-700',
      refunded: 'bg-danger-100 text-danger-700',
    };
    const labels: Record<string, string> = {
      pending: 'Pending',
      deposit_paid: 'Deposit Paid',
      confirmed: 'Confirmed',
      balance_paid: 'Balance Paid',
      completed: 'Completed',
      cancelled: 'Cancelled',
      refunded: 'Refunded',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status] || 'bg-warm-100 text-warm-600'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (date: string) => {
    // Append noon time to date-only strings to prevent timezone shift
    // (e.g., '2026-03-21' parsed as UTC midnight shows as Mar 20 in US timezones)
    const dateStr = date.includes('T') ? date : date + 'T12:00:00';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // SDA-266: compact date for payout rows ("Aug 3, 2026"); date-only strings
  // get the noon suffix to prevent the UTC-midnight timezone shift.
  const formatShortDate = (date: string) => {
    const dateStr = date.includes('T') ? date : date + 'T12:00:00';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // SDA-266: same palette as the Payouts page status badges.
  const payoutStatusChip = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-warning-100 text-warning-700',
      processing: 'bg-warning-100 text-warning-700',
      paid: 'bg-success-100 text-success-700',
      failed: 'bg-danger-100 text-danger-700',
      cancelled: 'bg-warm-200 text-warm-600',
    };
    return (
      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-warm-100 text-warm-600'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div>
        <div className="h-10 bg-warm-200 rounded w-1/4 animate-pulse mb-8" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
              <div className="h-6 bg-warm-100 rounded w-1/3 mb-4" />
              <div className="space-y-3">
                <div className="h-4 bg-warm-100 rounded w-full" />
                <div className="h-4 bg-warm-100 rounded w-2/3" />
              </div>
            </div>
          </div>
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
              <div className="h-6 bg-warm-100 rounded w-1/2 mb-4" />
              <div className="h-4 bg-warm-100 rounded w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-warm-900 mb-4">Booking Not Found</h2>
        <Link to="/admin/bookings" className="text-primary-600 hover:text-primary-700">
          Back to Bookings
        </Link>
      </div>
    );
  }

  // Refunds live as cumulative refunded_cents ON the original payment rows
  // (the charge.refunded webhook convention) — a refunded payment keeps its
  // gross amount_cents and moves to status refunded/partially_refunded.
  // totalPaid stays gross collected; a refund does not re-open the balance.
  const PAID_STATUSES = ['succeeded', 'partially_refunded', 'refunded'];
  const totalPaid = booking.payments?.reduce((sum, p) =>
    p.type !== 'refund' && PAID_STATUSES.includes(p.status) ? sum + p.amount_cents : sum, 0
  ) || 0;
  const totalRefunded = booking.payments?.reduce((sum, p) =>
    p.type !== 'refund' ? sum + (p.refunded_cents ?? 0) : sum, 0
  ) || 0;
  const balanceDue = (booking.total_cents || 0) - totalPaid;
  // Stripe-collected money not yet refunded — drives the Process Refund button.
  const maxRefundable = booking.payments?.reduce((sum, p) =>
    p.type !== 'refund' && p.stripe_payment_intent_id && PAID_STATUSES.includes(p.status)
      ? sum + (p.amount_cents - (p.refunded_cents ?? 0))
      : sum, 0
  ) || 0;

  // SDA-232: a deposit is owed when the booking still has an unpaid deposit
  // remainder. The admin booking-detail GET returns deposit_cents +
  // deposit_paid_cents (NOT deposit_amount_cents, and deposit_paid_at is not a
  // real column), so the remaining deposit is the correct, accurate signal.
  const depositCents = booking.deposit_cents ?? booking.deposit_amount_cents ?? 0;
  const depositPaidCents = booking.deposit_paid_cents ?? 0;
  const depositOwed =
    depositCents - depositPaidCents > 0 && booking.status !== 'cancelled';

  // SDA-265: the balance owed as the SERVER computes it
  // (total - deposit_paid_cents - balance_paid_cents), which is what
  // send-balance-link charges and emails.
  //
  // Deliberately NOT the `balanceDue` above: that sums the `payments` array, and
  // this feature exists for bookings whose deposits were recorded OFFLINE — those
  // have no payments row, so the payments-derived figure reports the full booking
  // total as outstanding. Using it here would show the admin (and the confirm
  // dialog) a balance far larger than the studio actually owes.
  const balanceLinkDue = booking.balance_due_cents;

  // The balance-link button. Deposit first — while a deposit is owed the admin
  // should send the DEPOSIT link, because that flow saves the card and arms
  // auto-charge. Statuses match the endpoint's own gate so the button can never
  // appear where the server would answer INVALID_STATE.
  const balanceLinkEligible =
    !depositOwed &&
    balanceLinkDue > 0 &&
    booking.balance_status !== 'paid' &&
    booking.balance_status !== 'waived' &&
    ['pending', 'deposit_paid', 'confirmed'].includes(booking.status);

  // SDA-256: a booking is completable once its class has happened AND the money
  // is settled. 'confirmed' keeps its existing behaviour. The import-era
  // 'deposit_paid'/'balance_paid' states additionally require a settled balance —
  // completing a booking schedules faculty payouts, so an unpaid booking must
  // never be completable.
  const balanceSettled =
    booking.balance_status === 'paid' ||
    booking.balance_status === 'waived' ||
    balanceDue <= 0;
  const hasPastClass = booking.classes?.some((cls) => {
    const classDate = cls.scheduled_date;
    return classDate && new Date(classDate + 'T23:59:59') < new Date();
  }) ?? false;
  // SDA-296: on a multi-class booking each class is completed on its own date
  // (the booking button would complete the future ones and pay for them). The
  // button only comes back once every live class is done, so Ryan can close a
  // booking that was left open for an unsettled balance.
  const liveClasses = (booking.classes ?? []).filter((c) => c.status !== 'cancelled');
  const isMultiClass = (booking.classes?.length ?? 0) > 1;
  const allLiveClassesCompleted = liveClasses.length > 0 && liveClasses.every((c) => c.status === 'completed');
  const canMarkComplete =
    hasPastClass &&
    (booking.status === 'confirmed' ||
      ((booking.status === 'deposit_paid' || booking.status === 'balance_paid') &&
        balanceSettled)) &&
    (!isMultiClass || allLiveClassesCompleted);

  // SDA-296: the per-class link uses the same booking rule, but with the
  // server's balance (total - deposit paid - balance paid), which is what the
  // endpoint checks; the payments-derived balanceDue above can over-report
  // offline-recorded deposits.
  const classBalanceSettled =
    booking.balance_status === 'paid' ||
    booking.balance_status === 'waived' ||
    booking.balance_due_cents <= 0;
  const bookingAllowsClassComplete =
    booking.status === 'confirmed' ||
    ((booking.status === 'deposit_paid' || booking.status === 'balance_paid') && classBalanceSettled);
  const todayET = todayInTimezone();
  const canCompleteClass = (cls: AdminClass) =>
    isMultiClass &&
    bookingAllowsClassComplete &&
    cls.status !== 'completed' &&
    cls.status !== 'cancelled' &&
    !!cls.scheduled_date &&
    cls.scheduled_date <= todayET;

  // SDA-287: one row per unique faculty on the booking, for the Marketing Materials card.
  const headshotRows = buildFacultyHeadshotRows(booking.classes);

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/admin/bookings"
          className="inline-flex items-center text-sm text-warm-600 hover:text-warm-900 mb-4"
        >
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Bookings
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">
              Booking #{booking.booking_number || booking.id.slice(0, 8)}
            </h1>
            <span className="text-xs text-warm-400 font-mono">{booking.id.slice(0, 8)}</span>
            {getStatusBadge(booking.status)}
          </div>
          <div className="flex flex-wrap gap-2">
            {canMarkComplete && (
              <button
                onClick={() => setShowCompleteModal(true)}
                className="px-3 py-1.5 text-sm bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors"
              >
                Mark Complete
              </button>
            )}
            {/* Refund is gated on refundable Stripe money, NOT booking status —
                the real flow is cancel the booking first, then refund it. */}
            {maxRefundable > 0 && (
              <button
                onClick={() => {
                  setRefundAmount('');
                  setRefundReason('');
                  setRefundError(null);
                  setShowRefundModal(true);
                }}
                aria-label="Process Refund"
                className="px-3 py-1.5 text-sm border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
              >
                <span className="hidden sm:inline">Process </span>Refund
              </button>
            )}
            {booking.status !== 'cancelled' && booking.status !== 'completed' && (
              <button
                onClick={() => {
                  setNotifyStudio(true);
                  setShowCancelModal(true);
                }}
                className="px-3 py-1.5 text-sm border border-danger-300 text-danger-700 rounded-lg hover:bg-danger-50 transition-colors"
              >
                Cancel Booking
              </button>
            )}
          </div>
        </div>
      </div>

      {success && (
        <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-lg text-success-700">
          {success}
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Studio Info */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-warm-900 mb-4">Studio Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-warm-500">Studio Name</p>
                <p className="font-medium text-warm-900">{booking.studio?.name}</p>
              </div>
              <div>
                <p className="text-sm text-warm-500">Primary Contact</p>
                <p className="font-medium text-warm-900">
                  {booking.studio?.primary_contact?.first_name} {booking.studio?.primary_contact?.last_name}
                </p>
                <p className="text-sm text-warm-600">{booking.studio?.primary_contact?.email}</p>
                <p className="text-sm text-warm-600">{booking.studio?.primary_contact?.phone}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-sm text-warm-500">Address</p>
                <p className="font-medium text-warm-900">
                  {booking.studio?.address_line1}
                  {booking.studio?.address_line2 && <>, {booking.studio.address_line2}</>}
                  <br />
                  {booking.studio?.city}, {booking.studio?.state} {booking.studio?.zip_code}
                </p>
              </div>
            </div>
          </div>

          {/* Session Preferences */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-warm-900">Session Preferences</h2>
              {!editingSession && (
                <button
                  onClick={() => {
                    setSessionPref(booking.start_time_preference || 'default');
                    setSessionSimultaneous(booking.simultaneous_sessions || false);
                    setSessionTrigger(booking.simultaneous_trigger || null);
                    setEditingSession(true);
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Edit
                </button>
              )}
            </div>
            {editingSession ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">Start Time Preference</label>
                  <select
                    value={sessionPref}
                    onChange={(e) => setSessionPref(e.target.value as 'early' | 'default' | 'late')}
                    className="w-full border border-warm-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="default">Recommended start time</option>
                    <option value="early">Earlier start time</option>
                    <option value="late">Later start time</option>
                  </select>
                  <p className="text-xs text-warm-500 mt-1.5">
                    Recommended is the standard start time for this booking. Earlier or Later shift it accordingly.
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="simultaneous-sessions"
                      checked={sessionSimultaneous}
                      onChange={(e) => {
                        setSessionSimultaneous(e.target.checked);
                        if (!e.target.checked) setSessionTrigger(null);
                      }}
                      className="h-4 w-4 rounded border-warm-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="simultaneous-sessions" className="text-sm font-medium text-warm-700">
                      Simultaneous Sessions
                    </label>
                  </div>
                  <p className="text-xs text-warm-500 mt-1.5 ml-7">
                    Both sessions run at the same time in two separate studios, instead of back-to-back.
                  </p>
                </div>
                {sessionSimultaneous && (
                  <div>
                    <label className="block text-sm font-medium text-warm-700 mb-1">Trigger</label>
                    <select
                      value={sessionTrigger || ''}
                      onChange={(e) => setSessionTrigger(e.target.value ? e.target.value as 'matinee_threshold' | 'manual_checkbox' | 'distance_threshold' : null)}
                      className="w-full border border-warm-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">None</option>
                      <option value="matinee_threshold">Matinee Threshold</option>
                      <option value="manual_checkbox">Manual Checkbox</option>
                      <option value="distance_threshold">Distance Threshold</option>
                    </select>
                    <p className="text-xs text-warm-500 mt-1.5">
                      {sessionTrigger === 'matinee_threshold'
                        ? 'System auto-detected an early matinee — sessions must run at the same time so faculty can make the show.'
                        : sessionTrigger === 'manual_checkbox'
                          ? 'The studio manually requested both sessions to run at the same time in two separate studios.'
                          : sessionTrigger === 'distance_threshold'
                            ? 'Venue is 40+ miles from the studio — simultaneous sessions required to fit the schedule.'
                            : 'Select why simultaneous sessions are enabled.'}
                    </p>
                  </div>
                )}
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={async () => {
                      if (!id) return;
                      setSavingSession(true);
                      try {
                        const updated = await updateBooking(id, {
                          start_time_preference: sessionPref,
                          simultaneous_sessions: sessionSimultaneous,
                          simultaneous_trigger: sessionSimultaneous ? sessionTrigger : null,
                        });
                        setBooking((prev) => prev ? { ...prev, ...updated } : prev);
                        setEditingSession(false);
                      } catch (err) {
                        setError(err instanceof Error ? err.message : 'Failed to update session preferences');
                      } finally {
                        setSavingSession(false);
                      }
                    }}
                    disabled={savingSession}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
                  >
                    {savingSession ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setEditingSession(false)}
                    className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg text-sm font-medium hover:bg-warm-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  booking.start_time_preference === 'early' ? 'bg-amber-100 text-amber-800' :
                  booking.start_time_preference === 'late' ? 'bg-blue-100 text-blue-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {booking.start_time_preference === 'default' ? 'Recommended start time' :
                   booking.start_time_preference === 'early' ? 'Earlier start time' :
                   booking.start_time_preference === 'late' ? 'Later start time' :
                   'Recommended start time'}
                </span>
                {booking.simultaneous_sessions && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                    Simultaneous
                    {booking.simultaneous_trigger === 'matinee_threshold' ? ' (Matinee)' :
                     booking.simultaneous_trigger === 'manual_checkbox' ? ' (Manual)' :
                     booking.simultaneous_trigger === 'distance_threshold' ? ' (Distance)' : ''}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Classes */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-warm-900 mb-4">
              Classes ({booking.classes?.length || 0})
            </h2>
            <div className="space-y-4">
              {booking.classes?.map((cls) => (
                <div key={cls.id} className="border border-warm-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-warm-900">
                        {cls.tour_stop?.show?.name}
                      </h3>
                      <p className="text-sm text-warm-600">
                        {cls.tour_stop?.theater?.name} • {cls.tour_stop?.city?.name}, {cls.tour_stop?.city?.state}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-warm-900">
                        {formatCurrency(cls.price_cents || 0)}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        cls.status === 'completed' ? 'bg-warm-100 text-warm-600' :
                        cls.status === 'cancelled' ? 'bg-danger-100 text-danger-700' :
                        'bg-primary-100 text-primary-700'
                      }`}>
                        {cls.status}
                      </span>
                      {(cls.status !== 'completed' && cls.status !== 'cancelled') || canCompleteClass(cls) ? (
                        <div className="mt-2 flex flex-wrap justify-end gap-2">
                          {cls.status !== 'completed' && cls.status !== 'cancelled' && (
                            <button
                              onClick={() => handleOpenClassModal(cls)}
                              aria-label="Edit class details"
                              className="px-2.5 py-1 text-xs font-medium rounded-md border border-warm-300 text-warm-700 bg-white hover:bg-warm-50"
                            >
                              Edit
                            </button>
                          )}
                          {canCompleteClass(cls) && (
                            <button
                              onClick={() => setCompleteClassTarget(cls)}
                              className="px-2.5 py-1 text-xs font-medium rounded-md border border-success-600 bg-success-600 text-white hover:bg-success-700"
                            >
                              Mark Class Complete
                            </button>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-warm-500">Date</p>
                      <p className="font-medium text-warm-900">{formatDate(cls.scheduled_date)}</p>
                    </div>
                    <div>
                      <p className="text-warm-500">Time</p>
                      <p className="font-medium text-warm-900">{cls.time_slot ? formatTimeDisplay(cls.time_slot) : '—'}</p>
                    </div>
                    {(() => {
                      // SDA-269: admin shows the same age range the studio saw at
                      // booking time and the class-prep emails repeat.
                      const level = formatClassLevel(cls.student_level);
                      return (
                        <div>
                          <p className="text-warm-500">Level</p>
                          <p className="font-medium text-warm-900">{level.label}</p>
                          {level.ages && <p className="text-xs text-warm-500">{level.ages}</p>}
                        </div>
                      );
                    })()}
                  </div>
                  {cls.faculty ? (() => {
                    const facultyName = [cls.faculty.first_name, cls.faculty.last_name].filter(Boolean).join(' ')
                      || cls.faculty.email
                      || 'Unknown Faculty';
                    const initials = cls.faculty.first_name?.[0] || cls.faculty.last_name?.[0]
                      ? `${cls.faculty.first_name?.[0] || ''}${cls.faculty.last_name?.[0] || ''}`
                      : facultyName[0]?.toUpperCase() || '?';
                    return (
                    <div className="mt-3 pt-3 border-t border-warm-100">
                      <p className="text-sm text-warm-500">Assigned Faculty</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        {cls.faculty!.headshot_url ? (
                          <img
                            src={cls.faculty!.headshot_url}
                            alt={facultyName}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-primary-600 font-medium text-sm">
                              {initials}
                            </span>
                          </div>
                        )}
                        <span className="font-medium text-warm-900">
                          {facultyName}
                        </span>
                        {cls.faculty_assignment_status === 'accepted' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-700">
                            <span className="text-[10px]">&#x2713;</span>
                            Confirmed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-700">
                            Awaiting confirmation
                          </span>
                        )}
                        {cls.faculty_paid === true && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-700">
                            Paid
                          </span>
                        )}
                        {/* SDA-296: a completed class's faculty was paid for it — changing
                            or removing them afterwards would misstate who was paid. */}
                        {cls.status !== 'completed' && (
                          <>
                            <button
                              onClick={() => handleOpenFacultyModal(cls)}
                              className="ml-auto px-2.5 py-1 text-xs font-medium rounded-md border border-primary-300 text-primary-700 bg-white hover:bg-primary-50"
                            >
                              Change
                            </button>
                            <button
                              onClick={() => setUnassignTarget({ classId: cls.id, facultyName })}
                              className="px-2.5 py-1 text-xs font-medium rounded-md border border-danger-300 text-danger-700 bg-white hover:bg-danger-50"
                            >
                              Unassign
                            </button>
                          </>
                        )}
                        {cls.faculty_paid !== true &&
                          cls.status !== 'cancelled' &&
                          !(booking.faculty_payouts ?? []).some(
                            (p) => p.class_id === cls.id && ['pending', 'processing', 'paid'].includes(p.status)
                          ) && (
                            <button
                              onClick={() => setMarkPaidTarget({ classId: cls.id, facultyName })}
                              className="px-2.5 py-1 text-xs font-medium rounded-md border border-warm-300 text-warm-700 bg-white hover:bg-warm-50"
                            >
                              Mark Paid Externally
                            </button>
                          )}
                      </div>
                    </div>
                    );
                  })() : (
                    <div className="mt-3 pt-3 border-t border-warm-100">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-warm-500">No faculty assigned</p>
                        <button
                          onClick={() => handleOpenFacultyModal(cls)}
                          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Assign Faculty
                        </button>
                      </div>
                    </div>
                  )}
                  {cls.special_requests && (
                    <div className="mt-3 pt-3 border-t border-warm-100">
                      <p className="text-sm text-warm-500">Special Requests</p>
                      <p className="text-sm text-warm-700 mt-1">{cls.special_requests}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Marketing Materials */}
          {booking.marketing_materials && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">Marketing Materials</h2>
              <div className="flex items-center justify-between gap-3 mb-4 pb-4 border-b border-warm-100">
                <label htmlFor="marketing-request-status" className="text-sm font-medium text-warm-700">
                  Request status
                </label>
                <select
                  id="marketing-request-status"
                  value={booking.marketing_request_status ?? 'waiting_studio_material'}
                  disabled={savingRequestStatus === 'marketing'}
                  onChange={(e) =>
                    handleRequestStatusChange('marketing', {
                      marketing_request_status: e.target.value as 'waiting_studio_material' | 'in_progress' | 'complete',
                    })
                  }
                  className="border border-warm-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                >
                  {MARKETING_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {/* SDA-287: headshots sit OUTSIDE the completed/not-completed branch — the
                  collateral gets built before the studio returns its questionnaire. */}
              {headshotRows.length > 0 && (
                <div className="mb-4 pb-4 border-b border-warm-100">
                  <p className="text-sm font-medium text-warm-700">Faculty Headshots</p>
                  <div className="mt-2 space-y-2">
                    {headshotRows.map((row) => {
                      const initials =
                        row.name
                          .split(' ')
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((part) => part[0])
                          .join('')
                          .toUpperCase() || '?';
                      return (
                        <div key={row.facultyId} className="flex items-center gap-3">
                          {row.headshotUrl ? (
                            <img
                              src={row.headshotUrl}
                              alt={row.name}
                              className="w-10 h-10 rounded-full object-cover border border-warm-200"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-primary-600 font-medium text-sm">{initials}</span>
                            </div>
                          )}
                          {row.downloadHref && row.filename ? (
                            <>
                              <span className="text-sm text-warm-900">{row.name}</span>
                              {/* The ?download= param in the href is what forces the attachment
                                  cross-origin; the download attribute is belt-and-braces. */}
                              <a
                                href={row.downloadHref}
                                download={row.filename}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-primary-600 hover:text-primary-700 hover:underline ml-auto"
                              >
                                Download
                              </a>
                            </>
                          ) : (
                            <>
                              <span className="text-sm text-warm-500">{row.name}</span>
                              <span className="text-sm text-warm-400 ml-auto">No headshot uploaded</span>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {booking.marketing_materials_completed_at ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-success-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-warm-900">
                        Completed{booking.marketing_materials_completed_by === 'studio' ? ' by Studio' : ' by Admin'}
                      </p>
                      <p className="text-sm text-warm-500">
                        {new Date(booking.marketing_materials_completed_at).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                  {/* Submitted marketing material data */}
                  {(booking.marketing_logo_url || booking.marketing_signup_method || booking.marketing_class_cost) && (
                    <div className="border-t border-warm-100 pt-4 space-y-3">
                      {booking.marketing_logo_url && (
                        <div className="flex items-start gap-3">
                          <span className="text-sm font-medium text-warm-500 w-32 shrink-0">Studio Logo</span>
                          <a href={booking.marketing_logo_url} target="_blank" rel="noopener noreferrer">
                            <img src={booking.marketing_logo_url} alt="Studio logo" className="h-16 w-auto rounded border border-warm-200 hover:opacity-80 transition-opacity" />
                          </a>
                        </div>
                      )}
                      {booking.marketing_signup_method && (
                        <div className="flex items-start gap-3">
                          <span className="text-sm font-medium text-warm-500 w-32 shrink-0">Sign-up Method</span>
                          <span className="text-sm text-warm-900">{booking.marketing_signup_method}</span>
                        </div>
                      )}
                      {booking.marketing_class_cost && (
                        <div className="flex items-start gap-3">
                          <span className="text-sm font-medium text-warm-500 w-32 shrink-0">Class Cost</span>
                          <span className="text-sm text-warm-900">{booking.marketing_class_cost}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-warm-600 mb-4">
                    The studio has been asked to complete a marketing materials questionnaire. Mark as complete once materials have been received.
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={async () => {
                        if (!id) return;
                        setIsSendingReminder(true);
                        try {
                          const result = await sendMarketingMaterialsReminder(id);
                          setBooking((prev) => prev ? { ...prev, marketing_materials_last_reminder_at: result.reminder_sent_at } : prev);
                          setReminderSuccess(true);
                          setTimeout(() => setReminderSuccess(false), 3000);
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Failed to send reminder');
                        } finally {
                          setIsSendingReminder(false);
                        }
                      }}
                      disabled={
                        isSendingReminder ||
                        (booking.marketing_request_status != null &&
                          booking.marketing_request_status !== 'waiting_studio_material')
                      }
                      title={
                        booking.marketing_request_status != null &&
                        booking.marketing_request_status !== 'waiting_studio_material'
                          ? 'Reminders only apply while waiting for studio material'
                          : undefined
                      }
                      className="px-4 py-2 border border-primary-300 text-primary-600 rounded-lg text-sm font-medium hover:bg-primary-50 disabled:opacity-50 transition-colors"
                    >
                      {isSendingReminder ? 'Sending...' : 'Send Reminder'}
                    </button>
                    <button
                      onClick={async () => {
                        if (!id) return;
                        setIsProcessing(true);
                        try {
                          const updated = await updateBooking(id, { marketing_materials_completed: true });
                          setBooking((prev) => prev ? { ...prev, ...updated } : prev);
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Failed to mark materials complete');
                        } finally {
                          setIsProcessing(false);
                        }
                      }}
                      disabled={isProcessing}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
                    >
                      {isProcessing ? 'Saving...' : 'Mark Materials Complete'}
                    </button>
                  </div>
                  {reminderSuccess && (
                    <p className="text-sm text-success-600 mt-2">Reminder sent successfully!</p>
                  )}
                  {booking.marketing_materials_last_reminder_at && !reminderSuccess && (
                    <p className="text-sm text-warm-500 mt-2">
                      Last reminder sent: {new Date(booking.marketing_materials_last_reminder_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Group Tickets */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-warm-900 mb-4">Group Tickets</h2>
            {booking.group_ticket_inquiry ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3 pb-4 border-b border-warm-100">
                  <label htmlFor="group-ticket-status" className="text-sm font-medium text-warm-700">
                    Request status
                  </label>
                  <select
                    id="group-ticket-status"
                    value={booking.group_ticket_status ?? 'new'}
                    disabled={savingRequestStatus === 'group'}
                    onChange={(e) =>
                      handleRequestStatusChange('group', {
                        group_ticket_status: e.target.value as 'new' | 'waiting_ticket_order' | 'complete',
                      })
                    }
                    className="border border-warm-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                  >
                    {GROUP_TICKET_STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800">
                      Requested
                    </span>
                    <span className="text-sm text-warm-600">Studio requested group tickets (free)</span>
                  </div>
                  <button
                    onClick={() => setGroupTicketsConfirm({ action: 'remove' })}
                    className="text-sm text-danger-600 hover:text-danger-700 font-medium transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm text-warm-500">Not requested</p>
                <button
                  onClick={() => setGroupTicketsConfirm({ action: 'add' })}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                >
                  Add group tickets
                </button>
              </div>
            )}
          </div>

          {unassignTarget && (
            <ConfirmModal
              title="Unassign faculty"
              message={`Remove ${unassignTarget.facultyName} from this class? It will return to Unassigned and you can assign someone else.`}
              confirmLabel="Unassign"
              confirmVariant="danger"
              onCancel={() => setUnassignTarget(null)}
              onConfirm={handleUnassignFaculty}
            />
          )}

          {showDepositLinkConfirm && (
            <ConfirmModal
              title="Send deposit payment link"
              message="This emails the studio's primary contact a secure, login-gated link to pay the remaining deposit online. Paying through the link saves their card and arms the automatic balance charge. Offline-recorded payments do not."
              confirmLabel="Send link"
              confirmVariant="primary"
              onCancel={() => setShowDepositLinkConfirm(false)}
              onConfirm={handleSendDepositLink}
            />
          )}

          {showBalanceLinkConfirm && (
            <ConfirmModal
              title="Send balance payment link"
              message={
                <div className="space-y-2">
                  <div>
                    <span className="text-warm-500">To:</span>{' '}
                    {booking.studio?.primary_contact?.email ? (
                      <span className="font-semibold text-warm-900">
                        {booking.studio.primary_contact.email}
                      </span>
                    ) : (
                      <span className="font-semibold text-danger-600">
                        no primary contact on file
                      </span>
                    )}
                  </div>
                  <div>
                    <span className="text-warm-500">Amount:</span>{' '}
                    <span className="font-semibold text-warm-900">
                      {formatCurrency(Math.max(0, balanceLinkDue))}
                    </span>
                  </div>
                  <p>Emails a secure link to pay the remaining balance online.</p>
                  {booking.studio?.is_dormant && (
                    <p className="p-2 bg-warning-50 border border-warning-200 rounded-lg text-warning-800">
                      This studio has not activated its account yet — the email
                      takes them to a set-password screen first, then straight
                      to this booking.
                    </p>
                  )}
                </div>
              }
              confirmLabel="Send link"
              confirmVariant="primary"
              onCancel={() => setShowBalanceLinkConfirm(false)}
              onConfirm={handleSendBalanceLink}
            />
          )}

          {groupTicketsConfirm && (
            <ConfirmModal
              title={groupTicketsConfirm.action === 'add' ? 'Add Group Tickets' : 'Remove Group Tickets'}
              message={
                groupTicketsConfirm.action === 'add'
                  ? 'This will mark the booking as requesting group tickets. Tickets are free — no charge is made to the studio.'
                  : 'This will remove the group ticket request from this booking. Tickets are free — no charge is made or reversed.'
              }
              confirmLabel={groupTicketsConfirm.action === 'add' ? 'Add Group Tickets' : 'Remove'}
              confirmVariant={groupTicketsConfirm.action === 'add' ? 'primary' : 'danger'}
              onCancel={() => setGroupTicketsConfirm(null)}
              onConfirm={async () => {
                if (!id) return;
                const newValue = groupTicketsConfirm.action === 'add';
                setGroupTicketsConfirm(null);
                setIsProcessing(true);
                try {
                  const updated = await updateBooking(id, { group_ticket_inquiry: newValue });
                  setBooking((prev) => prev ? { ...prev, ...updated } : prev);
                  setSuccess(newValue ? 'Group tickets added.' : 'Group tickets removed.');
                  setTimeout(() => setSuccess(null), 5000);
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Failed to update group tickets');
                } finally {
                  setIsProcessing(false);
                }
              }}
            />
          )}

          {/* Balance Schedule — multi-class bookings charged on different dates */}
          {booking.balance_schedule && booking.status !== 'cancelled' && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-lg font-semibold text-warm-900">Balance Schedule</h2>
                <span className="text-sm text-warm-500">
                  {formatCurrency(booking.balance_schedule.remaining_cents)} left
                </span>
              </div>
              <BalanceSchedule schedule={booking.balance_schedule} audience="admin" />
            </div>
          )}

          {/* Payment History */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-warm-900 mb-4">Payment History</h2>
            {booking.payments && booking.payments.length > 0 ? (
              <div className="space-y-3">
                {booking.payments.map((payment) => (
                  <div
                    key={payment.id}
                    className="flex items-center justify-between py-3 border-b border-warm-100 last:border-0"
                  >
                    <div>
                      <p className="font-medium text-warm-900">
                        {payment.type === 'deposit' ? 'Deposit' :
                         payment.type === 'balance' ? 'Balance Payment' :
                         payment.type === 'refund' ? 'Refund' : payment.type}
                      </p>
                      <p className="text-sm text-warm-500">
                        {formatDateTime(payment.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold ${
                        payment.type === 'refund' ? 'text-danger-600' : 'text-warm-900'
                      }`}>
                        {payment.type === 'refund' ? '-' : ''}{formatCurrency(payment.amount_cents)}
                      </p>
                      {(payment.refunded_cents ?? 0) > 0 && (
                        <p className="text-sm text-danger-600">
                          Refunded -{formatCurrency(payment.refunded_cents ?? 0)}
                        </p>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        payment.status === 'succeeded' ? 'bg-success-100 text-success-700' :
                        payment.status === 'pending' ? 'bg-warning-100 text-warning-700' :
                        payment.status === 'refunded' || payment.status === 'partially_refunded' ? 'bg-warm-100 text-warm-700' :
                        'bg-danger-100 text-danger-700'
                      }`}>
                        {payment.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-warm-500">No payments recorded yet.</p>
            )}
          </div>

          {/* Internal Notes */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-warm-900">Internal Notes</h2>
              <button
                onClick={() => setShowNoteModal(true)}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                + Add Note
              </button>
            </div>
            {booking.notes && booking.notes.length > 0 ? (
              <div className="space-y-4">
                {booking.notes.map((note: BookingNote) => (
                  <div key={note.id} className="border-l-2 border-warm-200 pl-4">
                    {editingNoteId === note.id ? (
                      <>
                        <textarea
                          value={editingNoteText}
                          onChange={(e) => setEditingNoteText(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none text-sm"
                        />
                        <div className="flex justify-end gap-2 mt-2">
                          <button
                            onClick={() => { setEditingNoteId(null); setEditingNoteText(''); }}
                            className="text-xs text-warm-500 hover:text-warm-700"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleUpdateNote(note.id)}
                            disabled={isProcessing || !editingNoteText.trim()}
                            className="text-xs text-primary-600 hover:text-primary-700 disabled:opacity-50"
                          >
                            {isProcessing ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-warm-700">{note.note}</p>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-sm text-warm-500">
                            {note.user ? `${note.user.first_name} ${note.user.last_name}` : 'System'} • {formatDateTime(note.created_at)}
                          </p>
                          <div className="flex gap-3">
                            <button
                              onClick={() => { setEditingNoteId(note.id); setEditingNoteText(note.note); }}
                              className="text-xs text-warm-400 hover:text-primary-600"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteNote(note.id)}
                              className="text-xs text-warm-400 hover:text-danger-600"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-warm-500">No internal notes yet.</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Payment Summary */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-warm-900 mb-4">Payment Summary</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-warm-600">Subtotal</span>
                <span className="text-warm-900">{formatCurrency(booking.subtotal_cents || 0)}</span>
              </div>
              {(booking.travel_surcharge_cents || 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-warm-600">Travel Surcharge</span>
                  <span className="text-warm-900">{formatCurrency(booking.travel_surcharge_cents || 0)}</span>
                </div>
              )}
              {(booking.marketing_fee_cents || 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-warm-600">Marketing Materials</span>
                  <span className="text-warm-900">{formatCurrency(booking.marketing_fee_cents || 0)}</span>
                </div>
              )}
              <div className="flex justify-between pt-3 border-t border-warm-200">
                <span className="font-semibold text-warm-900">Total</span>
                <span className="font-semibold text-warm-900">{formatCurrency(booking.total_cents || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-warm-600">Paid</span>
                <span className="text-success-600">{formatCurrency(totalPaid)}</span>
              </div>
              {totalRefunded > 0 && (
                <div className="flex justify-between">
                  <span className="text-warm-600">Refunded</span>
                  <span className="text-danger-600">-{formatCurrency(totalRefunded)}</span>
                </div>
              )}
              <div className="flex justify-between pt-3 border-t border-warm-200">
                <span className="font-semibold text-warm-900">Balance Due</span>
                <span className={`font-semibold ${balanceDue > 0 ? 'text-danger-600' : 'text-success-600'}`}>
                  {formatCurrency(Math.max(0, balanceDue))}
                </span>
              </div>
              {booking.balance_status === 'waived' && (
                <p className="text-xs text-warm-500">Balance waived</p>
              )}
              <div className="flex justify-between gap-3" data-testid="card-on-file">
                <span className="text-warm-600 whitespace-nowrap">Card on file</span>
                <span className="text-warm-900 text-right text-sm">
                  {savedCard.status === 'loading'
                    ? 'Loading...'
                    : savedCard.status === 'error'
                    ? 'Unavailable'
                    : savedCard.card
                    ? describeSavedCard(savedCard.card)
                    : 'None'}
                </span>
              </div>
              {/* Manual reconcile actions: only when there's an outstanding
                  balance and it hasn't already been collected or waived. */}
              {balanceDue > 0 &&
                booking.balance_status !== 'paid' &&
                booking.balance_status !== 'waived' &&
                booking.status !== 'cancelled' && (
                <div className="flex gap-2 pt-3 border-t border-warm-200">
                  <button
                    onClick={() => {
                      setReconcileReference('');
                      setReconcileNote('');
                      setReconcileAmount('');
                      setReconcileAction('mark_paid');
                    }}
                    disabled={isProcessing}
                    className="flex-1 px-3 py-2 min-h-[44px] text-sm font-medium border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 disabled:opacity-50"
                  >
                    Mark balance paid
                  </button>
                  <button
                    onClick={() => {
                      setReconcileReference('');
                      setReconcileNote('');
                      setReconcileAction('waive');
                    }}
                    disabled={isProcessing}
                    className="flex-1 px-3 py-2 min-h-[44px] text-sm font-medium border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 disabled:opacity-50"
                  >
                    Waive balance
                  </button>
                </div>
              )}
              {/* SDA-232: email the studio a login-gated deposit payment link.
                  Only when a deposit is still owed and the booking isn't
                  cancelled. Paying via the link saves the card + arms auto-charge. */}
              {depositOwed && (
                <div className="pt-3 border-t border-warm-200">
                  <button
                    onClick={() => setShowDepositLinkConfirm(true)}
                    disabled={isProcessing}
                    className="w-full px-3 py-2 min-h-[44px] text-sm font-medium border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 disabled:opacity-50"
                  >
                    Send deposit payment link
                  </button>
                </div>
              )}
              {/* SDA-265: email the studio a login-gated balance payment link.
                  For the grandfathered bookings the balance-reminder cron skips,
                  this is the only nudge — dormant studios self-activate en route. */}
              {balanceLinkEligible && (
                <div className="pt-3 border-t border-warm-200">
                  <button
                    onClick={() => setShowBalanceLinkConfirm(true)}
                    disabled={isProcessing}
                    className="w-full px-3 py-2 min-h-[44px] text-sm font-medium border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 disabled:opacity-50"
                  >
                    Send balance payment link
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Faculty Payouts (SDA-266: close the loop on booking detail) */}
          {(() => {
            const payoutRows = booking.faculty_payouts ?? [];
            const classesWithFaculty = (booking.classes ?? []).filter(
              (c) => c.faculty && c.status !== 'cancelled'
            );
            if (payoutRows.length === 0 && classesWithFaculty.length === 0) return null;
            // Completed classes (SDA-296: per class, since one class of a
            // multi-class booking can complete on its own) whose faculty never
            // got a payout row (the Stripe-ready gate skipped them).
            const missingPayoutClasses = classesWithFaculty.filter(
              (c) =>
                c.status === 'completed' &&
                c.faculty_paid !== true &&
                !payoutRows.some((p) => p.class_id === c.id && p.status !== 'cancelled')
            );
            // SDA-276: classes marked paid externally never get a payout row —
            // explain the absence instead of leaving it unaddressed.
            const paidExternallyClasses = classesWithFaculty.filter(
              (c) =>
                c.faculty_paid === true &&
                !payoutRows.some((p) => p.class_id === c.id && p.status !== 'cancelled')
            );
            return (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-warm-900 mb-4">Faculty Payouts</h2>
                {payoutRows.length === 0 && missingPayoutClasses.length === 0 && paidExternallyClasses.length === 0 ? (
                  <p className="text-sm text-warm-500">
                    Payouts are scheduled when the booking is marked complete.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {payoutRows.map((p) => (
                      <div key={p.id} className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-warm-900">{p.faculty_name}</p>
                          {p.status === 'pending' && (
                            <p className="text-xs text-warm-500">
                              {p.scheduled_for
                                ? `Scheduled for ${formatShortDate(p.scheduled_for)}`
                                : 'Scheduled'}
                            </p>
                          )}
                          {p.status === 'paid' && (
                            <p className="text-xs text-success-600 flex items-center gap-1">
                              <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Paid{p.processed_at ? ` ${formatShortDate(p.processed_at)}` : ''}
                            </p>
                          )}
                          {p.status === 'failed' && (
                            <p className="text-xs text-danger-600">
                              {p.failure_reason || 'Transfer failed'}
                              {' '}
                              <Link to="/admin/payouts" className="text-primary-600 hover:underline whitespace-nowrap">
                                Retry on Payouts →
                              </Link>
                            </p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold text-warm-900">
                            {formatCurrency(p.amount_cents)}
                          </p>
                          {payoutStatusChip(p.status)}
                        </div>
                      </div>
                    ))}
                    {missingPayoutClasses.map((c) => (
                      <div key={c.id} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-xs text-amber-800">
                          <span className="font-medium">
                            {c.faculty?.first_name} {c.faculty?.last_name}
                          </span>
                          {' — '}No payout created — faculty not Stripe-ready when completed.
                        </p>
                      </div>
                    ))}
                    {paidExternallyClasses.map((c) => (
                      <div key={c.id} className="flex items-center justify-between gap-3">
                        <p className="text-xs text-warm-500">
                          <span className="font-medium text-warm-700">
                            {c.faculty?.first_name} {c.faculty?.last_name}
                          </span>
                          {' — Paid externally'}
                          {c.faculty_paid_at ? `, ${formatShortDate(c.faculty_paid_at)}` : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Timeline */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-warm-900 mb-4">Timeline</h2>
            <div className="relative">
              {(() => {
                const getTimelineDisplay = (action: string, metadata: Record<string, unknown> | null) => {
                  switch (action) {
                    case 'booking_created':
                    case 'booking_confirmed':
                    case 'manual_booking_created':
                      return {
                        dot: 'bg-success-500',
                        label: action === 'manual_booking_created' ? 'Manual Booking Created' :
                               action === 'booking_confirmed' ? 'Booking Confirmed' : 'Booking Created',
                        detail: null,
                      };
                    case 'deposit_paid':
                      return { dot: 'bg-success-500', label: 'Deposit Paid', detail: null };
                    case 'balance_paid':
                      return { dot: 'bg-success-500', label: 'Balance Paid', detail: null };
                    case 'faculty_assigned':
                      return {
                        dot: 'bg-primary-500',
                        label: 'Faculty Assigned',
                        detail: metadata?.faculty_name ? String(metadata.faculty_name) : null,
                      };
                    case 'faculty_reassigned':
                      return {
                        dot: 'bg-primary-500',
                        label: 'Faculty Reassigned',
                        detail: metadata?.faculty_name ? String(metadata.faculty_name) : null,
                      };
                    case 'faculty_unassigned':
                      return {
                        dot: 'bg-danger-500',
                        label: 'Faculty Unassigned',
                        detail: metadata?.faculty_name ? String(metadata.faculty_name) : null,
                      };
                    case 'faculty_marked_paid_external':
                      return {
                        dot: 'bg-success-500',
                        label: 'Faculty Marked Paid Externally',
                        detail: [
                          metadata?.faculty_name ? String(metadata.faculty_name) : null,
                          metadata?.reference ? String(metadata.reference) : null,
                        ].filter(Boolean).join(' — ') || null,
                      };
                    case 'assignment_accepted':
                      return {
                        dot: 'bg-success-500',
                        label: 'Faculty Accepted',
                        detail: metadata?.faculty_name ? String(metadata.faculty_name) : null,
                      };
                    case 'assignment_declined':
                      return {
                        dot: 'bg-danger-500',
                        label: 'Faculty Declined',
                        detail: [
                          metadata?.faculty_name ? String(metadata.faculty_name) : null,
                          metadata?.decline_reason ? String(metadata.decline_reason) : null,
                        ].filter(Boolean).join(' — ') || null,
                      };
                    case 'assignment_expired':
                      return {
                        dot: 'bg-warning-500',
                        label: 'Assignment Expired',
                        detail: metadata?.faculty_name
                          ? `${String(metadata.faculty_name)} — did not respond within 72 hours`
                          : 'Faculty did not respond within 72 hours',
                      };
                    case 'refund_processed':
                      return {
                        dot: 'bg-warning-500',
                        label: 'Refund Processed',
                        detail: metadata?.amount_cents
                          ? formatCurrency(Number(metadata.amount_cents))
                          : null,
                      };
                    case 'faculty_payout_paid':
                      return {
                        dot: 'bg-success-500',
                        label: 'Faculty Payout Sent',
                        detail: metadata?.amount_cents
                          ? formatCurrency(Number(metadata.amount_cents))
                          : null,
                      };
                    case 'faculty_payout_failed':
                      return {
                        dot: 'bg-danger-500',
                        label: 'Faculty Payout Failed',
                        detail: [
                          metadata?.amount_cents ? formatCurrency(Number(metadata.amount_cents)) : null,
                          metadata?.reason ? String(metadata.reason) : null,
                        ].filter(Boolean).join(' — ') || null,
                      };
                    case 'faculty_payout_skipped':
                      return {
                        dot: 'bg-warning-500',
                        label: 'Faculty Payout Skipped',
                        detail: 'Faculty not Stripe-ready when the class was completed',
                      };
                    case 'class_completed':
                      return {
                        dot: 'bg-success-500',
                        label: 'Class Marked Complete',
                        detail:
                          metadata?.payout === 'created'
                            ? 'Faculty payout scheduled'
                            : metadata?.payout === 'exists'
                              ? 'A payout already existed'
                              : null,
                      };
                    case 'faculty_payout_retried':
                      return {
                        dot: 'bg-primary-500',
                        label: 'Faculty Payout Retried',
                        detail: null,
                      };
                    case 'booking_completed':
                      return {
                        dot: 'bg-success-500',
                        label: 'Booking Completed',
                        detail: metadata?.auto ? 'Completed automatically after the last class' : null,
                      };
                    case 'booking_cancelled':
                      return {
                        dot: 'bg-danger-500',
                        label: 'Booking Cancelled',
                        detail: metadata?.reason ? String(metadata.reason) : null,
                      };
                    case 'booking_modified':
                      return { dot: 'bg-warm-400', label: 'Booking Modified', detail: null };
                    default:
                      return {
                        dot: 'bg-warm-400',
                        label: action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                        detail: null,
                      };
                  }
                };

                // Use timeline from API if available, otherwise fall back to timestamp fields
                if (booking.timeline && booking.timeline.length > 0) {
                  return (
                    <div className="space-y-0">
                      {booking.timeline.map((entry, idx) => {
                        const display = getTimelineDisplay(entry.action, entry.metadata);
                        const isLast = idx === booking.timeline!.length - 1;
                        return (
                          <div key={entry.id} className="relative flex gap-3 pb-4 last:pb-0">
                            {/* Vertical connector line */}
                            {!isLast && (
                              <div className="absolute left-[3px] top-4 bottom-0 w-0.5 bg-warm-200" />
                            )}
                            <div className={`relative z-10 w-2 h-2 mt-2 rounded-full shrink-0 ${display.dot}`} />
                            <div className="min-w-0">
                              <p className="font-medium text-warm-900 text-sm">{display.label}</p>
                              {display.detail && (
                                <p className="text-sm text-warm-600">{display.detail}</p>
                              )}
                              {entry.description && (
                                <p className="text-sm text-warm-600">{entry.description}</p>
                              )}
                              <p className="text-xs text-warm-400 mt-0.5">{formatDateTime(entry.created_at)}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                // Fallback: build timeline from timestamp fields
                const fallbackEntries: { label: string; dot: string; date: string; detail?: string }[] = [];
                fallbackEntries.push({ label: 'Booking Created', dot: 'bg-success-500', date: booking.created_at });
                if (booking.deposit_paid_at) {
                  fallbackEntries.push({ label: 'Deposit Paid', dot: 'bg-success-500', date: booking.deposit_paid_at });
                }
                if (booking.balance_paid_at) {
                  fallbackEntries.push({ label: 'Balance Paid', dot: 'bg-success-500', date: booking.balance_paid_at });
                }
                if (booking.cancelled_at) {
                  fallbackEntries.push({
                    label: 'Cancelled',
                    dot: 'bg-danger-500',
                    date: booking.cancelled_at,
                    detail: booking.cancellation_reason || undefined,
                  });
                }

                return (
                  <div className="space-y-0">
                    {fallbackEntries.map((entry, idx) => {
                      const isLast = idx === fallbackEntries.length - 1;
                      return (
                        <div key={idx} className="relative flex gap-3 pb-4 last:pb-0">
                          {!isLast && (
                            <div className="absolute left-[3px] top-3 bottom-0 w-0.5 bg-warm-200" />
                          )}
                          <div className={`w-2 h-2 mt-2 rounded-full shrink-0 ${entry.dot}`} />
                          <div className="min-w-0">
                            <p className="font-medium text-warm-900 text-sm">{entry.label}</p>
                            {entry.detail && (
                              <p className="text-sm text-warm-600">{entry.detail}</p>
                            )}
                            <p className="text-xs text-warm-400 mt-0.5">{formatDateTime(entry.date)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Inquiry Link */}
          {booking.inquiry_id && (
            <div className="bg-warm-50 rounded-xl p-4">
              <p className="text-sm text-warm-600">This booking was converted from an inquiry.</p>
              <Link
                to={`/admin/inquiries/${booking.inquiry_id}`}
                className="text-sm text-primary-600 hover:text-primary-700 mt-1 inline-block"
              >
                View Original Inquiry →
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* SDA-296: Mark Class Complete */}
      {completeClassTarget && (() => {
        const target = completeClassTarget;
        const showName = target.tour_stop?.show?.name;
        const row = (booking.payout_preview ?? []).find((r) => r.class_id === target.id);
        const payment = (booking.class_payments ?? []).find((p) => p.class_id === target.id);
        const unpaidShare = !!payment && payment.paid_cents < payment.share_cents;
        return (
          <ConfirmModal
            title="Mark class complete"
            confirmLabel="Mark Class Complete"
            onCancel={() => setCompleteClassTarget(null)}
            onConfirm={handleCompleteClass}
            message={
              <div className="space-y-3">
                <p>
                  {showName ? `${showName}, ` : ''}
                  {formatShortDate(target.scheduled_date)}
                </p>
                {!row ? (
                  <p className="text-warm-500">No faculty payout is due for this class.</p>
                ) : row.stripe_ready ? (
                  <p className="bg-warm-50 rounded-lg px-3 py-2 text-warm-800">
                    {row.faculty_name} — {formatCurrency(row.amount_cents)} payout will be scheduled for today and sent within about 15 minutes.
                  </p>
                ) : (
                  <p className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                    <span className="font-medium">
                      {row.faculty_name} — {formatCurrency(row.amount_cents)}
                    </span>
                    {' — '}No payout will be created — faculty not Stripe-ready.
                  </p>
                )}
                {unpaidShare && payment && (
                  <p className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                    The studio has paid {formatCurrency(payment.paid_cents)} of this class's {formatCurrency(payment.share_cents)} share.
                    {row?.stripe_ready
                      ? ` Completing it still pays ${row.faculty_name} ${formatCurrency(row.amount_cents)} today.`
                      : ''}
                  </p>
                )}
              </div>
            }
          />
        );
      })()}

      {/* Cancel Modal */}
      {/* Complete Booking Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-warm-900 mb-4">Complete Booking</h3>
            <p className="text-warm-600 mb-2">
              Are you sure you want to mark this booking as completed?
            </p>
            {/* SDA-266: server-computed preview of exactly what completion schedules */}
            {(() => {
              const preview = booking.payout_preview ?? [];
              if (preview.length === 0) {
                return (
                  <p className="text-sm text-warm-500 mb-6">
                    This will complete all classes. No faculty payouts are due.
                  </p>
                );
              }
              const showName = booking.tour_stop?.show?.name;
              const ready = preview.filter((r) => r.stripe_ready);
              const readyTotal = ready.reduce((sum, r) => sum + r.amount_cents, 0);
              return (
                <div className="mb-6">
                  <p className="text-sm text-warm-500 mb-2">
                    This will complete all classes and schedule these faculty payouts:
                  </p>
                  <div className="space-y-2">
                    {preview.map((row) =>
                      row.stripe_ready ? (
                        <div
                          key={row.class_id}
                          className="flex justify-between gap-2 text-sm bg-warm-50 rounded-lg px-3 py-2"
                        >
                          <span className="text-warm-800">
                            {row.faculty_name} — {formatCurrency(row.amount_cents)}
                            {showName ? ` — ${showName},` : ' —'} {formatShortDate(row.class_date)}
                          </span>
                        </div>
                      ) : (
                        <div
                          key={row.class_id}
                          className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800"
                        >
                          <span className="font-medium">
                            {row.faculty_name} — {formatCurrency(row.amount_cents)}
                          </span>
                          {' — '}No payout will be created — faculty not Stripe-ready.
                        </div>
                      )
                    )}
                  </div>
                  {ready.length > 0 ? (
                    <p className="text-sm text-warm-600 mt-3">
                      {ready.length} payout{ready.length === 1 ? '' : 's'} totaling{' '}
                      {formatCurrency(readyTotal)} will be scheduled
                      {booking.next_payout_date
                        ? ` for ${formatShortDate(booking.next_payout_date)} and sent within ~15 minutes`
                        : ''}
                      .
                    </p>
                  ) : (
                    <p className="text-sm text-warm-600 mt-3">No payouts will be scheduled.</p>
                  )}
                </div>
              );
            })()}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowCompleteModal(false)}
                disabled={isProcessing}
                className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    setIsProcessing(true);
                    setError(null);
                    await updateBooking(id!, { status: 'completed' });
                    setShowCompleteModal(false);
                    if (id) await loadBooking(id);
                    setSuccess('Booking marked as completed. Faculty payouts have been scheduled and will be sent shortly.');
                    setTimeout(() => setSuccess(null), 5000);
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to mark complete');
                  } finally {
                    setIsProcessing(false);
                  }
                }}
                disabled={isProcessing}
                className="px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:opacity-50"
              >
                {isProcessing ? 'Completing...' : 'Yes, Complete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4 overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold text-warm-900 mb-4">Cancel Booking</h3>
            <p className="text-warm-600 mb-4">
              Are you sure you want to cancel this booking? This action cannot be undone.
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation..."
              required
              rows={3}
              className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none mb-4"
            />
            <div className="mb-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="notify-studio-on-cancel"
                  checked={notifyStudio}
                  onChange={(e) => setNotifyStudio(e.target.checked)}
                  className="h-4 w-4 rounded border-warm-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="notify-studio-on-cancel" className="text-sm font-medium text-warm-700">
                  Email the studio about this cancellation
                </label>
              </div>
              <p className="text-xs text-warm-500 mt-1.5 ml-7">
                {notifyStudio
                  ? 'The reason above is included in the studio\'s email.'
                  : 'The studio will not be notified.'}
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50"
              >
                Keep Booking
              </button>
              <button
                onClick={handleCancelBooking}
                disabled={isProcessing || !cancelReason.trim()}
                className="px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 disabled:opacity-50"
              >
                {isProcessing ? 'Cancelling...' : 'Cancel Booking'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4 overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold text-warm-900 mb-2">Process Refund</h3>
            <p className="text-sm text-warm-600 mb-4">
              Refunds the money to the card it was collected on via Stripe. When the amount spans
              multiple charges, the balance payment is refunded before the deposit.
            </p>
            {refundError && (
              <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700">
                {refundError}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label htmlFor="refund-amount" className="block text-sm font-medium text-warm-700 mb-1">
                  Refund Amount
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-warm-500">$</span>
                  <input
                    id="refund-amount"
                    type="number"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    max={maxRefundable / 100}
                    className="w-full pl-8 pr-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <p className="text-sm text-warm-500 mt-1">
                  Maximum refundable: {formatCurrency(maxRefundable)}
                </p>
              </div>
              <div>
                <label htmlFor="refund-reason" className="block text-sm font-medium text-warm-700 mb-1">
                  Reason for Refund
                </label>
                <textarea
                  id="refund-reason"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Reason for refund..."
                  required
                  rows={3}
                  className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowRefundModal(false)}
                className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRefund}
                disabled={
                  isProcessing ||
                  !refundAmount ||
                  !refundReason.trim() ||
                  Math.round(parseFloat(refundAmount) * 100) > maxRefundable
                }
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {isProcessing ? 'Processing...' : 'Process Refund'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reconcile Balance Modal (Mark paid / Waive) */}
      {reconcileAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4 overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold text-warm-900 mb-2">
              {reconcileAction === 'mark_paid' ? 'Mark Balance Paid' : 'Waive Balance'}
            </h3>
            <p className="text-sm text-warm-600 mb-4">
              {reconcileAction === 'mark_paid' ? (
                <>
                  Records an external (non-Stripe) payment of{' '}
                  <span className="font-semibold text-warm-900">{formatCurrency(Math.max(0, balanceDue))}</span>{' '}
                  — use this when the balance was already collected outside the system (e.g. QuickBooks).
                </>
              ) : (
                <>
                  Forgives the outstanding balance of{' '}
                  <span className="font-semibold text-warm-900">{formatCurrency(Math.max(0, balanceDue))}</span>.
                </>
              )}{' '}
              This stops the automatic balance charge for this booking.
            </p>
            <div className="space-y-4">
              {reconcileAction === 'mark_paid' && (
                <div>
                  <label htmlFor="reconcile-amount" className="block text-sm font-medium text-warm-700 mb-1">
                    Amount to record <span className="text-warm-400 font-normal">(optional)</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-500">$</span>
                    <input
                      id="reconcile-amount"
                      type="number"
                      min="0"
                      step="0.01"
                      value={reconcileAmount}
                      onChange={(e) => setReconcileAmount(e.target.value)}
                      placeholder={(Math.max(0, balanceDue) / 100).toFixed(2)}
                      className="w-full pl-7 pr-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <p className="text-xs text-warm-500 mt-1">
                    Leave blank to record the full remaining balance. A partial amount keeps the
                    booking eligible for the remaining auto-charge. Offline-recorded money does not
                    save a card or arm auto-charge.
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Reference <span className="text-warm-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={reconcileReference}
                  onChange={(e) => setReconcileReference(e.target.value)}
                  placeholder="e.g. QBO invoice #1234"
                  maxLength={500}
                  className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Note <span className="text-warm-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={reconcileNote}
                  onChange={(e) => setReconcileNote(e.target.value)}
                  placeholder="Internal note..."
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setReconcileAction(null);
                  setReconcileAmount('');
                }}
                disabled={isProcessing}
                className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReconcileBalance}
                disabled={isProcessing}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {isProcessing
                  ? 'Processing...'
                  : reconcileAction === 'mark_paid'
                  ? 'Mark Paid'
                  : 'Waive Balance'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark Faculty Paid Externally Modal (SDA-276) */}
      {markPaidTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4 overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold text-warm-900 mb-2">Mark Faculty Paid Externally</h3>
            <p className="text-sm text-warm-600 mb-4">
              Record that {markPaidTarget.facultyName} was already paid outside the system (e.g. via QBO).
              No money will be sent, and completing this booking will not schedule a payout for this class.
            </p>
            <div className="space-y-4">
              <div>
                <label htmlFor="mark-paid-reference" className="block text-sm font-medium text-warm-700 mb-1">
                  Reference <span className="text-warm-400 font-normal">(optional)</span>
                </label>
                <input
                  id="mark-paid-reference"
                  type="text"
                  value={markPaidReference}
                  onChange={(e) => setMarkPaidReference(e.target.value)}
                  placeholder="e.g. QBO check #1234"
                  maxLength={500}
                  className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="mark-paid-note" className="block text-sm font-medium text-warm-700 mb-1">
                  Note <span className="text-warm-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="mark-paid-note"
                  value={markPaidNote}
                  onChange={(e) => setMarkPaidNote(e.target.value)}
                  placeholder="Internal note..."
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setMarkPaidTarget(null);
                  setMarkPaidReference('');
                  setMarkPaidNote('');
                }}
                disabled={isProcessing}
                className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkFacultyPaidExternal}
                disabled={isProcessing}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {isProcessing ? 'Processing...' : 'Mark Paid'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4 overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold text-warm-900 mb-4">Add Internal Note</h3>
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Enter your note..."
              required
              rows={4}
              className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowNoteModal(false)}
                className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddNote}
                disabled={isProcessing || !newNote.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {isProcessing ? 'Adding...' : 'Add Note'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Faculty Modal */}
      {showFacultyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4 overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold text-warm-900 mb-4">
              Assign Faculty
              {selectedClassForFaculty?.showName && (
                <span className="block text-sm font-normal text-warm-500 mt-1">
                  for {selectedClassForFaculty.showName}
                </span>
              )}
            </h3>

            {loadingFaculty ? (
              <div className="py-8 text-center">
                <div className="inline-block w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-warm-500 mt-2">Loading faculty...</p>
              </div>
            ) : availableFaculty.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-warm-500">No faculty are certified for this show.</p>
                <p className="text-sm text-warm-400 mt-1">
                  Make sure faculty members are certified for this show.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Select Faculty Member
                  </label>
                  <select
                    value={selectedFacultyId}
                    onChange={(e) => setSelectedFacultyId(e.target.value)}
                    className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Choose a faculty member...</option>
                    {availableFaculty.map((faculty) => {
                      // SDA-215: surface onboarding state for non-active faculty
                      // so admins know who hasn't finished onboarding yet.
                      const statusSuffix =
                        faculty.status && faculty.status !== 'active' && FACULTY_STATUS_LABELS[faculty.status]
                          ? ` — ${FACULTY_STATUS_LABELS[faculty.status]}`
                          : '';
                      return (
                        <option key={faculty.id} value={faculty.id}>
                          {faculty.first_name} {faculty.last_name}
                          {statusSuffix}
                          {faculty.email ? ` (${faculty.email})` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {selectedFacultyId && (
                  <div className="bg-warm-50 rounded-lg p-3">
                    {(() => {
                      const selected = availableFaculty.find(f => f.id === selectedFacultyId);
                      if (!selected) return null;
                      return (
                        <div className="flex items-center gap-3">
                          {selected.headshot_url ? (
                            <img
                              src={selected.headshot_url}
                              alt={`${selected.first_name} ${selected.last_name}`}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-primary-600 font-medium">
                                {selected.first_name?.[0]}{selected.last_name?.[0]}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-warm-900">
                              {selected.first_name} {selected.last_name}
                            </p>
                            <p className="text-sm text-warm-500">{selected.email}</p>
                            {/* SDA-215: heads-up when the picked faculty isn't active yet — the
                                assignment saves, but NO email is ever sent for it (not even on
                                activation); they just see it in their dashboard once active. */}
                            {selected.status && selected.status !== 'active' && (
                              <p className="text-xs text-warning-700 mt-1">
                                {FACULTY_STATUS_LABELS[selected.status] || 'Not active'} — assignment saves
                                now. No email is sent; they'll see it in their dashboard once their profile
                                is active.
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowFacultyModal(false);
                  setSelectedClassForFaculty(null);
                  setSelectedFacultyId('');
                }}
                className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignFaculty}
                disabled={isProcessing || !selectedFacultyId}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {isProcessing ? 'Assigning...' : 'Assign Faculty'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Class Modal */}
      {showClassModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4 overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold text-warm-900 mb-4">Edit Class Details</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">Date</label>
                <input
                  type="date"
                  value={classDate}
                  onChange={(e) => setClassDate(e.target.value)}
                  className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">Start time</label>
                  <input
                    type="time"
                    value={classStartTime}
                    onChange={(e) => setClassStartTime(e.target.value)}
                    className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">End time</label>
                  <input
                    type="time"
                    value={classEndTime}
                    onChange={(e) => setClassEndTime(e.target.value)}
                    className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">Level</label>
                <select
                  value={classLevel}
                  onChange={(e) => setClassLevel(e.target.value as 'all_levels' | 'beginner' | 'intermediate' | 'advanced')}
                  className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all_levels">All Levels</option>
                  <option value="beginner">Beginner (Ages 9+)</option>
                  <option value="intermediate">Intermediate (Ages 9-12)</option>
                  <option value="advanced">Advanced (Ages 12+)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">Expected students</label>
                <input
                  type="number"
                  min={1}
                  max={999}
                  value={classStudents}
                  onChange={(e) => setClassStudents(e.target.value)}
                  className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {classError && (
                <p className="text-sm text-danger-600">{classError}</p>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowClassModal(false);
                  setEditingClassId(null);
                  setClassError(null);
                }}
                className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveClass}
                disabled={savingClass}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {savingClass ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
